import { RootState } from '@/store/store'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { ChevronRightIcon } from '@radix-ui/react-icons'
import { useState } from 'react'
import Markdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import remarkGfm from 'remark-gfm'
import rehypeTagNameChange from '@/util/rehype-custom-plugins/rehype-tag-swap'
import { ArrowLeft, ArrowRight, XClose } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import SkillChipIcon from '@/skills/components/SkillChipIcon'
import clsx from 'clsx'
import { useCreateNewAgentChat } from '@/agent/conversations/hooks/useNewAgentChat'
import { SkillsActions } from '@/skills/store/slice'
import { DEVICE_TYPE, useDeviceType } from '@/hooks/useDeviceType'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent, SkillSource } from '@/analytics/schema/events.schema'
import { Modal, ModalClose, ModalTitle, ModalTrigger } from '@/components/modal/Modal'
import { ModalContainer } from '@/components/modal/ModalContainer'

interface AllSkillsDialogBaseProps {
  /** If provided, the dialog will be opened from the current chat */
  conversationId?: string
}

interface AllSkillsDialogUncontrolledProps extends AllSkillsDialogBaseProps {
  open?: undefined
  onOpenChange?: undefined
}

interface AllSkillsDialogControlledProps extends AllSkillsDialogBaseProps {
  /** When provided with onOpenChange, the dialog is controlled and the trigger is hidden (e.g. when opening from another button) */
  open: boolean
  /** Required in controlled mode so the dialog can close. */
  onOpenChange: (open: boolean) => void
}

type AllSkillsDialogProps = AllSkillsDialogUncontrolledProps | AllSkillsDialogControlledProps

export default function AllSkillsDialog(props: AllSkillsDialogProps) {
  const { conversationId, open, onOpenChange } = props
  const isControlled = open !== undefined
  const { createNewAgentChatWithSkill } = useCreateNewAgentChat()
  const dispatch = useAppDispatch()
  const { trackEvent } = useAnalytics()

  // Local state
  const [selectedSkillEnum, setSelectedSkillEnum] = useState<string | null>(null)

  const deviceType = useDeviceType()
  const isMobile = deviceType === DEVICE_TYPE.MOBILE

  // Get the active matter id - for creating a new chat with a skill
  const activeMatterId = useAppSelector((state: RootState) => state.mattersState.activeMatterId)

  // Get a list of the skills from the store
  const skillsData = useAppSelector((state: RootState) => state.skillsState.skillsContentData)
  const skills = skillsData.skills
  const skillContent = skills.find((skill) => skill.skill === selectedSkillEnum)

  // Skill Content Component
  function SkillContent() {
    if (!skillContent) {
      return (
        <div className="grow flex items-center justify-center h-full">
          <div className="text-brand-neutral-500">Select a skill to see details.</div>
        </div>
      )
    }

    // Else: render the skill content
    const skillTitle = skillContent.label
    const skillLongDescription = skillContent.long_description

    return (
      <div className={'p-6 pb-4'}>
        <div className="flex items-center gap-2 mb-4 text-neutral-950">
          <SkillChipIcon skill={skillContent.skill} />
          <h3 className="text-lg font-semibold">{skillTitle}</h3>
        </div>
        <div id="chat-response-bubble">
          <Markdown
            className="markdown-bubble-container"
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[
              rehypeRaw,
              [
                // Swap pre and code tags with div tags to fix markdown rendering issues
                rehypeTagNameChange,
                [
                  { tagName: 'pre', newTagName: 'div' },
                  { tagName: 'code', newTagName: 'div' },
                ],
              ],
            ]}
          >
            {skillLongDescription}
          </Markdown>
        </div>
      </div>
    )
  }

  function handleStartNow() {
    if (!selectedSkillEnum) return

    if (conversationId) {
      dispatch(
        SkillsActions.initializeConversationWithSkill({
          conversationId,
          skill: selectedSkillEnum,
          skillSource: SkillSource.modal,
        })
      )
    } else {
      createNewAgentChatWithSkill(true, activeMatterId, selectedSkillEnum, SkillSource.modal)
    }

    // Track prompt submission (modal)
    trackEvent(AnalyticsEvent.SkillPromptSubmitted, {
      skill: selectedSkillEnum,
      skillSource: SkillSource.modal,
    })
  }

  // Dynamic title for the dialog
  const dialogTitleContent =
    isMobile && selectedSkillEnum ? (
      <button
        className={'flex items-center justify-start gap-x-2 text-brand-neutral-950 font-semibold text-sm mt-1 pb-1 pl-4'}
        onClick={() => setSelectedSkillEnum(null)}
      >
        <ArrowLeft className="w-3 h-3" /> All Skills
      </button>
    ) : (
      'All Skills'
    )

  return (
    <Modal
      open={isControlled ? open : undefined}
      onOpenChange={(nextOpen) => {
        if (nextOpen) {
          trackEvent(AnalyticsEvent.SkillsModalOpened, { skillSource: SkillSource.modal })
        }
        if (!nextOpen) setSelectedSkillEnum(null)
        onOpenChange?.(nextOpen)
      }}
    >
      {!isControlled && (
        <ModalTrigger asChild>
          <button
            type="button"
            className={'flex flex-nowrap items-center gap-x-1 text-brand-500 px-2 py-0.5 rounded-md hover:bg-brand-50 transition-all duration-300'}
          >
            <span className={'text-sm font-medium leading-5'}>All Skills</span>
            <ChevronRightIcon className="fill-current" />
          </button>
        </ModalTrigger>
      )}
      {/* If mobile or tablet, show the full height dialog, otherwise show the md height dialog */}
      <ModalContainer width="xl" height={isMobile ? 'lg' : 'md'} aria-describedby={undefined}>
        {/* Title bar */}
        <div className="relative grow-0 shrink-0 basis-[40px]">
          <ModalTitle className="block text-center py-3 text-lg font-semibold text-brand-neutral-950 border-b border-brand-neutral-200">
            {dialogTitleContent}
          </ModalTitle>
          <ModalClose asChild>
            <button
              className="absolute right-6 top-[10px] w-7 h-7 rounded-full flex items-center justify-center text-brand-neutral-600 hover:bg-brand-neutral-200 transition-all duration-300"
              aria-label="Close"
              type="button"
            >
              <XClose className="size-6" />
            </button>
          </ModalClose>
        </div>

        {skills.length === 0 && (
          <div className={'flex flex-col items-center justify-center'}>
            <div className={'text-brand-neutral-500 text-sm'}>There was an error loading skills.</div>
          </div>
        )}

        <div className={clsx(isMobile && 'flex flex-col overflow-y-scroll', 'flex grow rounded-b-2xl overflow-hidden')}>
          {/* Left: scrollable list of skills */}
          <div
            className={clsx(
              isMobile && !!selectedSkillEnum ? 'hidden' : '',
              `sm:flex-grow-0 sm:flex-shrink-0 overflow-y-scroll border-r border-brand-neutral-200`
            )}
          >
            <nav>
              {skills.map((skill) => {
                const active = skill.skill === selectedSkillEnum

                return (
                  <div
                    key={skill.skill}
                    className={`w-full font-semibold text-sm text-left p-4 border-b border-brand-neutral-200 last:border-b-0 hover:bg-brand-neutral-100 cursor-pointer transition-all duration-300 ${
                      active ? 'text-brand-500 bg-brand-neutral-200' : 'text-brand-neutral-800'
                    }`}
                    onClick={() => {
                      setSelectedSkillEnum(skill.skill)

                      // Track skill selection within modal
                      trackEvent(AnalyticsEvent.SkillsModalSelected, { skill: skill.skill })
                    }}
                  >
                    <div className="flex justify-between items-center">
                      <span>{skill.label}</span>
                      <ChevronRightIcon className="w-5 h-5 md:hidden" />
                    </div>
                  </div>
                )
              })}
            </nav>
          </div>

          {/* Right: details of selected skill */}
          <div
            className={clsx(
              isMobile && !selectedSkillEnum ? 'hidden' : '', // Mobile breakpoint and no skill selected, hide the details
              'grow flex flex-col'
            )}
          >
            <div className={'grow overflow-y-scroll'}>
              <SkillContent />
            </div>

            {/* Bottom: Start Now button */}
            {selectedSkillEnum && (
              <div className={'grow-0 shrink-0 basis-[54px] border-t border-brand-neutral-200 flex items-center justify-end md:py-3 md:px-6 py-4 px-6'}>
                <ModalClose asChild>
                  <Button
                    type="button"
                    variant="outlined"
                    size={isMobile ? 'md' : 'sm'}
                    onClick={() => handleStartNow()}
                    className="w-full sm:w-auto"
                    trailingIcon={<ArrowRight className="h-4 w-4" />}
                  >
                    Start Now
                  </Button>
                </ModalClose>
              </div>
            )}
          </div>
        </div>
      </ModalContainer>
    </Modal>
  )
}
